import { type AssetType, getAssetLabel } from 'src/data/asset';

import { type GenerationTool } from './const';
import { getToolContentType, getToolDescription, getToolTitle } from './toolProps';
import {
  isFreestyleGenerationTool,
  isFreestyleOrImproverTool,
  isImageGenerationTool,
  mapGenerationTool,
} from './utils';

/**
 * Options for formatting variation display names
 */
export interface VariationDisplayNameOptions {
  /** Asset type for the variation */
  assetType?: AssetType;
  /** Whether the variation has an image */
  hasImage?: boolean;
  /** Whether this is an image-only variation (no text) */
  isImageOnly?: boolean;
  /** Whether to use title instead of contentType */
  useTitle?: boolean;
  /** Custom step name for multi-prompt variations */
  stepName?: string;
}

/**
 * Format display name for a tool based on options
 * Centralized function to ensure consistent naming across the UI
 *
 * @param tool - Generation tool
 * @param options - Options for formatting the name
 * @returns Formatted display name
 *
 */
export const formatToolDisplayName = (
  tool: GenerationTool,
  options: VariationDisplayNameOptions = {}
): string => {
  const { assetType, hasImage = false, isImageOnly = false, useTitle = false, stepName } = options;

  const mappedTool = mapGenerationTool(tool);
  const contentType = getToolContentType(mappedTool);
  const description = getToolDescription(mappedTool);
  const title = getToolTitle(mappedTool);
  // Image-only or freestyle with image
  if (isImageOnly || (hasImage && isFreestyleGenerationTool(mappedTool))) {
    return 'Image';
  }

  // Freestyle / Basic improver → General Text
  if (isFreestyleOrImproverTool(mappedTool) && !hasImage && !useTitle) {
    return 'General Text';
  }

  // Image generation tools
  if (isImageGenerationTool(mappedTool)) {
    const baseName = contentType || title || '';
    return stepName ? `${baseName} (${stepName})` : baseName;
  }

  const parts: string[] = [];

  // Title / ContentType
  if (useTitle && title) {
    parts.push(title);
  } else if (contentType) {
    parts.push(contentType);
  } else if (title) {
    parts.push(title);
  }

  let assetPart: string | undefined;

  // hasImage has highest priority
  if (hasImage) {
    assetPart = 'Image';
  } else if (description) {
    assetPart = description;
  } else if (assetType) {
    assetPart = getAssetLabel(assetType);
  }

  if (assetPart && !useTitle) {
    parts.push(assetPart);
  }

  let result = parts.join(' - ');

  if (stepName) {
    result += ` (${stepName})`;
  }

  return result || (assetType ? getAssetLabel(assetType) : '');
};
